/**
 * @file `Chapter` — namespacer for `lab.chapter.*` covering everything
 *       the `/chapter/:courseId/:partId/:chapterId` route reads or
 *       writes: the combined chapter fetch, engagement switching, and
 *       per-machine terminal lifecycle.
 * @module @labcraft/lab/src/client/chapter
 *
 * Per the workspace Client-shape rule: `CoreLifecycle` namespacer owned
 * by the parent `Lab`. Holds references to both transports (http for
 * the `GET /api/courses/.../chapter` + `POST /api/courses/engagement`
 * surface; ws for the per-machine `TerminalSub` subscriptions). Logs
 * and metrics bubble under `lab.chapter.*`.
 *
 * Internal `Terminal` helper holds the per-machine WS state — open
 * subscription, role/presence cache, resize-debounce timer, close
 * handlers. Terminals are NOT `adopt`-ed: their membership churns
 * across chapter navigations and `adopt` is sticky for the parent's
 * lifetime. The namespacer holds plain references and calls
 * `disconnect()` on each Terminal when it drops; `onStop` runs the
 * teardown again as a safety net.
 *
 * Frame channel split (mirrors `TerminalSub`):
 *   - Binary (pty I/O bytes) → `Terminal.write` / `Terminal.onData`.
 *     Passes through the `TypedSubscription`'s binary channel
 *     unvalidated.
 *   - Text JSON envelopes → `Terminal.resize` / `Terminal.sendFocus` /
 *     `Terminal.sendWrongTurn` / `Terminal.sendRecovered` /
 *     `Terminal.runCheck` (client→server), `Terminal.onRole` /
 *     `onLifecycle` / `onEngagement` / `onCommand` / `onMentor` /
 *     `onMentorPending` and the `probe-result` correlation (server→client).
 *     Validated against `TerminalSub.clientFrame` / `serverFrame`.
 */

import { CoreClass, CoreError, CoreLifecycle } from '@labcraft/core'
import type {
  BinaryCallback,
  CloseCallback,
  StatusCallback,
  SubscriptionStatus,
  TypedSubscription,
  Unsubscribe,
} from '@labcraft/core/frontend'
import type { HttpTransport, WsTransport } from './index.ts'
import {
  DeleteEngagementRoute,
  GetChapterRoute,
  GetPartAssessmentRoute,
  PostEngagementRoute,
  PostEngagementTouchRoute,
  PostPartFeedbackRoute,
  PostSuspendMachineRoute,
  PostRestartMachineRoute,
  type ChapterResponse,
  type EngagementResponse,
  type Machine,
  type PartAssessmentResponse,
  type PartFeedbackRequest,
} from '../shared/routes.ts'
import { TerminalSub } from '../shared/terminal_subscription.ts'
import type {
  TerminalClientFrame,
  TerminalServerFrame,
} from '../shared/terminal_subscription.ts'
import type {
  AtCapacityPayload,
  BudgetExhaustedPayload,
  CommandPayload,
  FocusPayload,
  LifecycleFrame,
  MachineLifecycleReason,
  MachineLifecycleState,
  MentorFramePayload,
  MentorPendingPayload,
  RecoveredPayload,
  Role,
  WrongTurnPayload,
} from '../shared/ws_messages.ts'

/**
 * One in-flight state probe awaiting its `probe-result` (#1149) —
 * the resolve/reject pair of the `runCheck` promise plus the deadline
 * timer that rejects it if the result never arrives.
 *
 * @property resolve - Settles `runCheck` with the satisfied verdict.
 * @property reject - Rejects `runCheck` (timeout / disconnect); the
 *   course-kit scheduler treats a rejection as "not satisfied".
 * @property timer - The deadline timer, cleared on settle.
 */
type PendingProbe = {
  resolve: (result: { satisfied: boolean }) => void
  reject: (err: CoreError) => void
  timer: ReturnType<typeof setTimeout>
}

////////////////////////////////////////////////////////////////////////////////
// Public callback types
////////////////////////////////////////////////////////////////////////////////

/** Subscribe handler signature for `Terminal.onRole`. */
export type RoleCallback = (role: Role) => void

/** Subscribe handler signature for `Terminal.onLifecycle`. */
export type LifecycleCallback = (frame: LifecycleFrame) => void

/**
 * Subscribe handler signature for `Terminal.onEngagement` (#1059) — the
 * engagement-TTL deadline (epoch ms), carried on its own frame rather
 * than stamped onto the verbatim-forwarded lifecycle frame. Fires on
 * bridge open and on each lifecycle transition; drives the pre-expiry
 * warning banner.
 */
export type EngagementCallback = (expiresAt: number) => void

/**
 * Subscribe handler signature for `Terminal.onCommand` — one completed
 * command event from the OSC 133 pipeline (#627/#628), which course-kit
 * matches against the active objective (#629).
 */
export type CommandCallback = (payload: CommandPayload) => void

/**
 * Subscribe handler signature for `Terminal.onBudgetExhausted` (#834) —
 * the learner hit the daily free-tier VM-time cap. Carries the upgrade
 * CTA URL; the UI dims the terminal (read-only) and renders the
 * conversion prompt. The VM was snapshotted, so a resume after upgrade /
 * tomorrow restores exactly where they hit the wall.
 */
export type BudgetExhaustedCallback = (payload: BudgetExhaustedPayload) => void

/**
 * Subscribe handler signature for `Terminal.onMentor` (#1148 / #1566) —
 * one processed mentor disposition (mentor → lab → SPA). Carries the
 * disposition (`held` / `silent` / `nudge` / `encouragement` / …) plus
 * `rung` (on a nudge) and `text` (on a text-bearing disposition).
 * course-kit's `<ChapterProvider>` keys the MentorDock's per-objective
 * transcript + pending states off it; always advisory, never gating.
 */
export type MentorFrameCallback = (payload: MentorFramePayload) => void

/**
 * Subscribe handler signature for `Terminal.onMentorPending` (#1566) — the
 * lab-owned "AI looking…" beat, minted only on a real observation relay and
 * resolved by the matching `onMentor` frame.
 */
export type MentorPendingCallback = (payload: MentorPendingPayload) => void

/**
 * Pool-level close-callback signature. Carries `machineId` so the
 * subscriber knows which machine's connection ended; `code`/`reason`
 * mirror the per-Terminal `onClose` callback.
 */
export type PoolCloseCallback = (
  machineId: string,
  code: number,
  reason: string,
) => void

////////////////////////////////////////////////////////////////////////////////
// Terminal helper
////////////////////////////////////////////////////////////////////////////////

/**
 * Per-machine terminal state — owns one typed WS subscription, the
 * service-local handler sets, and the resize debounce. Constructed
 * dynamically by `Chapter.attachTerminals(...)`; not adopted because
 * membership churns across chapter navigations.
 *
 * Extends `CoreClass` (observability, no framework lifecycle) — the
 * application-level `attach` / `disconnect` lifecycle is bare code:
 * the chapter namespacer calls them explicitly.
 */
export class Terminal extends CoreClass {
  private readonly ws: WsTransport

  private sub: TypedSubscription<
    TerminalClientFrame,
    TerminalServerFrame
  > | null = null
  private subHandlerUnsubs: Unsubscribe[] = []

  // Service-local handler sets. Forwarded from whichever underlying
  // `sub` is current.
  private readonly dataHandlers = new Set<BinaryCallback>()
  private readonly statusHandlers = new Set<StatusCallback>()
  private readonly closeHandlers = new Set<CloseCallback>()
  private readonly roleHandlers = new Set<RoleCallback>()
  private readonly lifecycleHandlers = new Set<LifecycleCallback>()
  private readonly engagementHandlers = new Set<EngagementCallback>()
  private readonly commandHandlers = new Set<CommandCallback>()
  private readonly budgetExhaustedHandlers = new Set<BudgetExhaustedCallback>()
  private readonly mentorHandlers = new Set<MentorFrameCallback>()
  private readonly mentorPendingHandlers = new Set<MentorPendingCallback>()

  // Most recent lifecycle state pushed from the server (synthesized
  // on attach, then updated on every backend transition). Exposed
  // via `lifecycleState` so a late-subscribed component sees the
  // current value immediately.
  private _lifecycleState: MachineLifecycleState | null = null
  // Most recent lifecycle-transition reason (#1056) — the trigger the
  // hypervisor (or lab) stamped on the last reclaim/terminal frame.
  // The pool consults it when the WS closes to pick a per-machine
  // recovery instead of one page-level takeover.
  private _lastReason: MachineLifecycleReason | null = null
  // Most recent `at-capacity` payload (umbrella #1257 Part E, #1284): the
  // learner's place in line + whether push is available, buffered from the
  // frame that lands just before the 4429 close so the chapter page can render
  // the at-capacity overlay with it.
  private _lastAtCapacity: AtCapacityPayload | null = null
  // Most recent `budget-exhausted` payload (#834/#1315): the upgrade URL,
  // buffered from the frame lab sends just before terminating the VM on a
  // spent free-tier cap. The chapter page reads it in its onClose
  // `budget-exhausted` branch to render the out-of-time takeover — the wall
  // is not a reconnectable eviction, so it never routes through the
  // resumable "Wake it up" surface.
  private _lastBudgetExhausted: BudgetExhaustedPayload | null = null
  // Most recent engagement-TTL deadline from lab's `engagement` frame
  // (#1059). Drives the pre-expiry warning banner once
  // `expiresAt - now < 5min`.
  private _expiresAt: number | null = null

  // Tracks the most recent status seen from any underlying sub so we
  // can answer `.status` synchronously.
  private _status: SubscriptionStatus = 'disconnected'

  // Latest `focus` payload handed to `sendFocus` (#1148). Remembered so
  // every fresh connect re-arms the bridge's mentor context — a focus
  // sent while the WS is still dialing drops silently in the transport,
  // and a reconnected bridge starts with no context. Deliberately NOT
  // reset by `attach()` — the pool's silent reconnect swaps the socket
  // without the provider re-firing focus.
  private _lastFocus: FocusPayload | null = null

  // Tracks the most recent server-pushed role so a late-subscribed
  // React component can render the right chrome immediately.
  // Initialized to null on every fresh `attach()`.
  private _role: Role | null = null

  // Last (machineId, terminalId) pair we attached to — held so
  // `Chapter.attachTerminals` can detect a "same machineId, new
  // terminalId" change without dragging the wire URL through state.
  private _attached: { machineId: string; terminalId: string } | null = null

  // In-flight state probes keyed by request id (#1149). `runCheck`
  // registers here; the matching `probe-result` (or the deadline /
  // teardown) settles + removes the entry.
  private readonly pendingProbes = new Map<string, PendingProbe>()
  // Deadline for one probe round-trip: the server-side guest timeout
  // is 5s, so 10s covers it + the HTTP hop with margin; past that the
  // result is treated as not-satisfied by the scheduler.
  private readonly PROBE_TIMEOUT_MS = 10_000

  // Debounce state for `resize()`. ResizeObserver fires per animation
  // frame during a drag (60/s); without debouncing each tick would
  // round-trip a WS frame + a pty.resize + an in-guest stty cycle.
  // We hold the latest cols/rows and fire ~120ms after the last
  // update — well clear of the human-perceptible delay after release
  // and much cheaper than per-frame spam.
  private resizePending: { cols: number; rows: number } | null = null
  private resizeTimer: ReturnType<typeof setTimeout> | null = null
  private readonly RESIZE_DEBOUNCE_MS = 120

  constructor(machineId: string, ws: WsTransport) {
    super({ id: machineId })
    this.ws = ws
  }

  //////////////////////////////////////////////////////////////////////////////
  // Lifecycle (application-level — not framework hooks)
  //////////////////////////////////////////////////////////////////////////////

  /**
   * Open a fresh typed subscription to the lab pty proxy for
   * `(machineId, terminalId)`. Replaces any prior connection (the
   * prior `sub` is silently torn down — local close handlers do
   * **not** fire, because from the consumer's perspective the
   * connection is being swapped, not ended). Service-local handlers
   * stay attached across the swap.
   *
   * @param machineId - Author-named machine slot from the chapter
   *   recipe (`'shell'`, `'client'`, …).
   * @param terminalId - Author-named terminal slot within the
   *   machine. v1 supports a single serial-console pty per VM; any
   *   non-empty slug resolves to it.
   */
  /**
   * Re-open the WS against the same `(machineId, terminalId)` this
   * Terminal last attached to (#1056) — the chapter page's one silent
   * reconnect before it surfaces an eviction takeover. No-op if the
   * Terminal was never attached (nothing to re-open).
   */
  reattach(): void {
    if (!this._attached) return
    this.attach(this._attached.machineId, this._attached.terminalId)
  }

  attach(machineId: string, terminalId: string): void {
    this.teardownSilent()
    // Reset cached server-state on every fresh connection — the
    // backend sends a fresh `role` + `lifecycle` envelope on attach, so
    // late subscribers will see a value within one round-trip.
    this._role = null
    this._lifecycleState = null
    this._lastReason = null
    this._expiresAt = null
    this._attached = { machineId, terminalId }
    const sub = this.ws.subscribe(TerminalSub, {
      params: { machineId, terminalId },
    })
    this.sub = sub
    this.subHandlerUnsubs = [
      sub.onBinary(bytes => {
        for (const h of this.dataHandlers) {
          try {
            h(bytes)
          } catch (err) {
            this.warn('onData', CoreError.from(err))
          }
        }
      }),
      sub.onData(frame => this.dispatchEnvelope(frame)),
      sub.onStatus(status => {
        this._status = status
        // #1148: (re)arm the mentor focus on every fresh connect — the
        // bridge on the other end is new (or reconnected) and holds no
        // context until a focus frame reaches it.
        if (status === 'connected' && this._lastFocus) {
          try {
            sub.send({ type: 'focus', payload: this._lastFocus })
          } catch (err) {
            this.warn('sendFocus', CoreError.from(err))
          }
        }
        for (const h of this.statusHandlers) {
          try {
            h(status)
          } catch (err) {
            this.warn('onStatus', CoreError.from(err))
          }
        }
      }),
      sub.onClose((code, reason) => {
        for (const h of this.closeHandlers) {
          try {
            h(code, reason)
          } catch (err) {
            this.warn('onClose', CoreError.from(err))
          }
        }
      }),
    ]
  }

  /**
   * Dispatch an inbound typed server-frame to the matching per-type
   * handler set. The frame has already been parsed against
   * `TerminalSub.serverFrame` by the underlying `TypedSubscription`,
   * so the switch can branch directly on `frame.type` with no
   * defensive re-validation.
   */
  private dispatchEnvelope(frame: TerminalServerFrame): void {
    switch (frame.type) {
      case 'role':
        this._role = frame.payload.role
        for (const h of this.roleHandlers) {
          try {
            h(frame.payload.role)
          } catch (err) {
            this.warn('onRole', CoreError.from(err))
          }
        }
        return
      case 'lifecycle':
        this._lifecycleState = frame.state
        if (frame.reason) this._lastReason = frame.reason
        for (const h of this.lifecycleHandlers) {
          try {
            h(frame)
          } catch (err) {
            this.warn('onLifecycle', CoreError.from(err))
          }
        }
        return
      case 'engagement':
        // #1059: the engagement-TTL deadline is its own frame now, no
        // longer stamped onto the lifecycle frame lab forwards verbatim.
        this._expiresAt = frame.expiresAt
        for (const h of this.engagementHandlers) {
          try {
            h(frame.expiresAt)
          } catch (err) {
            this.warn('onEngagement', CoreError.from(err))
          }
        }
        return
      case 'command':
        for (const h of this.commandHandlers) {
          try {
            h(frame.payload)
          } catch (err) {
            this.warn('onCommand', CoreError.from(err))
          }
        }
        return
      case 'budget-exhausted':
        // Buffer the payload (upgrade URL) alongside firing the handlers:
        // the frame lands immediately before the VM terminates, and the
        // chapter page reads `lastBudgetExhausted` in its onClose
        // `budget-exhausted` branch (#834/#1315).
        this._lastBudgetExhausted = frame.payload
        for (const h of this.budgetExhaustedHandlers) {
          try {
            h(frame.payload)
          } catch (err) {
            this.warn('onBudgetExhausted', CoreError.from(err))
          }
        }
        return
      case 'at-capacity':
        // Buffer the at-capacity payload (place in line + whether push is
        // available). The frame lands immediately before the WS closes 4429;
        // the chapter page reads `lastAtCapacity` in its AT_CAPACITY onClose
        // branch to render "you're #N in line" (umbrella #1257 Part E, #1284).
        this._lastAtCapacity = frame.payload
        return
      case 'mentor':
        for (const h of this.mentorHandlers) {
          try {
            h(frame.payload)
          } catch (err) {
            this.warn('onMentor', CoreError.from(err))
          }
        }
        return
      case 'mentor-pending':
        for (const h of this.mentorPendingHandlers) {
          try {
            h(frame.payload)
          } catch (err) {
            this.warn('onMentorPending', CoreError.from(err))
          }
        }
        return
      case 'probe-result': {
        // #1149: settle the matching `runCheck` promise. Unmatched ids
        // (a result racing the deadline / a reconnect) drop silently —
        // the scheduler already treated the request as not-satisfied.
        const pending = this.pendingProbes.get(frame.payload.id)
        if (pending) {
          clearTimeout(pending.timer)
          this.pendingProbes.delete(frame.payload.id)
          pending.resolve({ satisfied: frame.payload.satisfied })
        }
        return
      }
    }
  }

  /**
   * Close the current subscription. Safe to call when no
   * subscription is active (no-op).
   *
   * Does **not** propagate a close event to locally-registered
   * `onClose` handlers — see the chapter-page mount comment for the
   * StrictMode rationale (umbrella #318).
   */
  disconnect(): void {
    this.teardownSilent()
  }

  /**
   * Currently-attached `(machineId, terminalId)` pair, or `null` if
   * never attached / disconnected.
   */
  get attached(): { machineId: string; terminalId: string } | null {
    return this._attached
  }

  /**
   * Tear down the current subscription **without** notifying local
   * subscribers. Shared between {@link attach} (swap one connection
   * for another) and {@link disconnect} (release on caller-initiated
   * teardown). Detaches forwarders first so the underlying sub's
   * close-1000 emit doesn't reach our local handler sets.
   */
  private teardownSilent(): void {
    for (const unsub of this.subHandlerUnsubs) unsub()
    this.subHandlerUnsubs = []
    if (this.sub) {
      this.sub.unsubscribe()
      this.sub = null
    }
    if (this.resizeTimer) {
      clearTimeout(this.resizeTimer)
      this.resizeTimer = null
    }
    this.resizePending = null
    // #1149: a probe's `probe-result` can never arrive on the torn-down
    // socket — reject the in-flight ones loud (the scheduler reads a
    // rejection as not-satisfied and re-probes on the next trigger).
    for (const [id, pending] of this.pendingProbes) {
      clearTimeout(pending.timer)
      pending.reject(
        new CoreError({
          code: 'LAB_PROBE_DISCONNECTED',
          message: `probe ${id} dropped: terminal disconnected`,
        }),
      )
    }
    this.pendingProbes.clear()
    this._attached = null
    this._status = 'disconnected'
  }

  //////////////////////////////////////////////////////////////////////////////
  // I/O
  //////////////////////////////////////////////////////////////////////////////

  /**
   * Write bytes to the pty (binary frame). Silently drops the frame
   * if no connection is open; the typing UX upstream will see no
   * echo, which is the expected behavior for "tried to type into a
   * dead terminal."
   */
  write(bytes: Uint8Array): void {
    this.sub?.sendBinary(bytes)
  }

  /**
   * Notify the pty of a new viewport size. Sent as a typed `resize`
   * envelope through the `clientFrame` channel — the lab proxy
   * forwards verbatim to the hypervisor. Debounced ~120ms to avoid round-tripping
   * a WS frame + pty.resize + in-guest stty per ResizeObserver tick.
   */
  resize(cols: number, rows: number): void {
    this.resizePending = { cols, rows }
    console.debug('[terminal] resize.queued', { cols, rows })
    if (this.resizeTimer) clearTimeout(this.resizeTimer)
    this.resizeTimer = setTimeout(() => {
      this.resizeTimer = null
      const pending = this.resizePending
      this.resizePending = null
      if (!pending) return
      console.debug('[terminal] resize.fired', {
        cols: pending.cols,
        rows: pending.rows,
        status: this.sub?.status ?? 'no-sub',
      })
      this.sub?.send({
        type: 'resize',
        payload: { cols: pending.cols, rows: pending.rows },
      })
    }, this.RESIZE_DEBOUNCE_MS)
  }

  /**
   * Send a typed `focus` envelope (#1148) — the browser names the
   * active objective/exercise plus its authored mentor-context so lab
   * can arm the per-engagement mentor session. The latest payload is
   * remembered and re-sent on every fresh connect: a focus fired while
   * the WS is still dialing would otherwise drop silently, leaving the
   * mentor context-less (observations ignored, no nudges) until the
   * next objective change. Not debounced — focus changes at
   * objective-completion cadence, not ResizeObserver cadence.
   */
  sendFocus(payload: FocusPayload): void {
    this._lastFocus = payload
    this.sub?.send({ type: 'focus', payload })
  }

  /**
   * Send a typed `wrong-turn` envelope (#1150) — report one
   * deterministic Tier-1 wrong-turn firing (course-kit's `rethink`
   * dispatch) up to lab, which persists it into `mentor_wrong_turns`
   * (#1432, the retirement / re-validation signal) and relays it to the
   * mentor. Fire-and-forget: a closed socket drops the frame silently
   * (same contract as `write`) — a lost report only under-counts an
   * aggregate, so unlike `sendFocus` the payload is NOT remembered or
   * re-sent on reconnect.
   *
   * @param payload - The firing's `{objectiveId, wrongTurnId}` pair.
   */
  sendWrongTurn(payload: WrongTurnPayload): void {
    this.sub?.send({ type: 'wrong-turn', payload })
  }

  /**
   * Send a typed `recovered` envelope (#1566) — the reader's scanner
   * matched a command that satisfied the active objective after a struggle.
   * Terminates at lab, which marks the episode capture RECOVERED and (gated
   * on the operator's encouragement setting) relays it to the mentor for the
   * success-after-struggle encouragement. Fire-and-forget like
   * {@link sendWrongTurn}: a closed socket drops it silently.
   *
   * @param payload - The recovery's `{objectiveId, cmd}` pair.
   */
  sendRecovered(payload: RecoveredPayload): void {
    this.sub?.send({ type: 'recovered', payload })
  }

  /**
   * Run one author-trusted state check in the machine's guest (#1149):
   * send a typed `probe` envelope with a fresh request id and resolve
   * on the matching `probe-result`. Rejects after
   * {@link Terminal.PROBE_TIMEOUT_MS} with no result (and on
   * disconnect) — course-kit's `StateGoalScheduler` treats a rejection
   * as "not satisfied" and re-probes on the next trigger, so a lost
   * probe degrades to "not yet" rather than an error surface. Rate
   * limiting is the scheduler's job; this method is a plain
   * request/response round-trip, mirroring how `sendFocus` /
   * `onCommand` ride the same socket.
   *
   * @param check - The `{via:'state'}` detector's check script.
   * @returns The satisfied verdict (`exit === 0` at the bridge).
   */
  runCheck(check: string): Promise<{ satisfied: boolean }> {
    const id = crypto.randomUUID()
    return new Promise((resolve, reject) => {
      const timer = setTimeout(() => {
        this.pendingProbes.delete(id)
        reject(
          new CoreError({
            code: 'LAB_PROBE_TIMEOUT',
            message: `probe ${id} timed out after ${this.PROBE_TIMEOUT_MS}ms`,
          }),
        )
      }, this.PROBE_TIMEOUT_MS)
      this.pendingProbes.set(id, { resolve, reject, timer })
      // A closed socket drops the frame silently (same contract as
      // `write`); the deadline / teardown rejection then settles the
      // promise.
      this.sub?.send({ type: 'probe', payload: { id, check } })
    })
  }

  //////////////////////////////////////////////////////////////////////////////
  // Subscriptions
  //////////////////////////////////////////////////////////////////////////////

  /** Current connection status. */
  get status(): SubscriptionStatus {
    return this._status
  }

  /**
   * Register a handler for inbound pty bytes. Returns an
   * `Unsubscribe` that detaches just this handler.
   */
  onData(handler: BinaryCallback): Unsubscribe {
    this.dataHandlers.add(handler)
    return () => {
      this.dataHandlers.delete(handler)
    }
  }

  /**
   * Register a status-change handler. The handler is called
   * immediately with the current status, then on every transition.
   */
  onStatus(handler: StatusCallback): Unsubscribe {
    this.statusHandlers.add(handler)
    handler(this._status)
    return () => {
      this.statusHandlers.delete(handler)
    }
  }

  /**
   * Register a handler for socket close events. Called with the
   * close `code` and the server-supplied reason (may be empty).
   */
  onClose(handler: CloseCallback): Unsubscribe {
    this.closeHandlers.add(handler)
    return () => {
      this.closeHandlers.delete(handler)
    }
  }

  /**
   * Most recent `role` value pushed by the backend, or `null` if no
   * `role` envelope has arrived yet on this connection.
   */
  get role(): Role | null {
    return this._role
  }

  /** Register a handler for `role` envelope frames. */
  onRole(handler: RoleCallback): Unsubscribe {
    this.roleHandlers.add(handler)
    return () => {
      this.roleHandlers.delete(handler)
    }
  }

  /**
   * Register a handler for `lifecycle` envelope frames — fires on
   * every VM state transition (booting → running → suspending →
   * suspended → resuming → running → expired). The most recent
   * value is also synchronously readable via `lifecycleState`.
   */
  onLifecycle(handler: LifecycleCallback): Unsubscribe {
    this.lifecycleHandlers.add(handler)
    return () => {
      this.lifecycleHandlers.delete(handler)
    }
  }

  /**
   * Register a handler for `engagement` frames (#1059) — the
   * engagement-TTL deadline, on its own channel now that lab no longer
   * stamps it onto the lifecycle frame. The most recent value is also
   * synchronously readable via `expiresAt`.
   */
  onEngagement(handler: EngagementCallback): Unsubscribe {
    this.engagementHandlers.add(handler)
    return () => {
      this.engagementHandlers.delete(handler)
    }
  }

  /**
   * Register a handler for `command` envelope frames — one completed
   * command event from the OSC 133 pipeline (#627/#628). course-kit's
   * `<ChapterProvider>` adapts this into a `CommandSource` to advance
   * objectives (#629).
   */
  onCommand(handler: CommandCallback): Unsubscribe {
    this.commandHandlers.add(handler)
    return () => {
      this.commandHandlers.delete(handler)
    }
  }

  /**
   * Register a handler for `budget-exhausted` frames (#834) — the
   * learner hit the daily free-tier VM-time cap. The UI dims the
   * terminal (read-only) and renders the upgrade CTA from the payload's
   * `upgradeUrl`.
   */
  onBudgetExhausted(handler: BudgetExhaustedCallback): Unsubscribe {
    this.budgetExhaustedHandlers.add(handler)
    return () => {
      this.budgetExhaustedHandlers.delete(handler)
    }
  }

  /**
   * Register a handler for `mentor` frames (#1148 / #1566) — one processed
   * mentor disposition relayed mentor → lab → SPA. Mirrors {@link onCommand}:
   * course-kit's `<ChapterProvider>` keys the MentorDock's per-objective
   * transcript + pending states off it. Advisory only — the mentor channel
   * never blocks advancement or the terminal.
   */
  onMentor(handler: MentorFrameCallback): Unsubscribe {
    this.mentorHandlers.add(handler)
    return () => {
      this.mentorHandlers.delete(handler)
    }
  }

  /**
   * Register a handler for `mentor-pending` frames (#1566) — the lab-owned
   * "AI looking…" beat, minted on a real observation relay and resolved by
   * the matching `mentor` frame. Advisory, like {@link onMentor}.
   */
  onMentorPending(handler: MentorPendingCallback): Unsubscribe {
    this.mentorPendingHandlers.add(handler)
    return () => {
      this.mentorPendingHandlers.delete(handler)
    }
  }

  /**
   * Most recent VM lifecycle state pushed by the server, or `null` if
   * no `lifecycle` frame has arrived since the last `attach`. The
   * backend synthesizes an initial lifecycle frame on every fresh WS
   * attach, so this resolves within one round-trip.
   */
  get lifecycleState(): MachineLifecycleState | null {
    return this._lifecycleState
  }

  /**
   * Most recent lifecycle-transition reason (#1056) — the trigger the
   * server stamped on the last reclaim/terminal frame, or `null` if no
   * reasoned transition has arrived since the last `attach`. The
   * chapter page consults this when the WS closes to render
   * reason-specific recovery copy per machine.
   */
  get lastReason(): MachineLifecycleReason | null {
    return this._lastReason
  }

  /**
   * Most recent `at-capacity` payload (umbrella #1257 Part E, #1284) — the
   * learner's place in line + whether push is available, from the frame lab
   * sends just before closing 4429. `null` until such a frame arrives. The
   * chapter page reads it in its AT_CAPACITY onClose branch to render the
   * at-capacity overlay with the position.
   */
  get lastAtCapacity(): AtCapacityPayload | null {
    return this._lastAtCapacity
  }

  /**
   * Most recent `budget-exhausted` payload (#834/#1315) — the upgrade URL,
   * from the frame lab sends just before terminating the VM on a spent
   * free-tier cap. `null` until such a frame arrives. The chapter page reads
   * it in its onClose `budget-exhausted` branch to render the out-of-time
   * takeover (the wall is not a reconnectable eviction).
   */
  get lastBudgetExhausted(): BudgetExhaustedPayload | null {
    return this._lastBudgetExhausted
  }

  /**
   * Most recent engagement-TTL deadline (ms since epoch) stamped by
   * lab on the last lifecycle frame. `null` until the first
   * `lifecycle` frame lands. Consumers compute `expiresAt - Date.now()`
   * to drive the pre-expiry banner.
   */
  get expiresAt(): number | null {
    return this._expiresAt
  }
}

////////////////////////////////////////////////////////////////////////////////
// Chapter namespacer
////////////////////////////////////////////////////////////////////////////////

/**
 * Namespacer for the `/chapter/...` route. Owns:
 *
 *   - `getChapter()`: combined chapter fetch (manifest + machines +
 *     conflict resolution).
 *   - `postEngagement()`: chapter-switching mutation.
 *   - Per-machine `Terminal` pool: diff-based `attachTerminals` /
 *     `detachTerminals` / `terminalFor`. The Terminal references the
 *     ws transport for opening subscriptions.
 *
 * `onStop` tears down every terminal as a safety net in case the
 * parent's lifecycle teardown runs before the page calls
 * `detachTerminals()` on unmount.
 */
export class Chapter extends CoreLifecycle<{ id?: string }> {
  private readonly http: HttpTransport
  private readonly ws: WsTransport

  private readonly terminals = new Map<string, Terminal>()
  /**
   * Most recent `terminalId` we attached each Terminal with (#1054).
   * Detects "same machineId, new terminalId" across chapter
   * navigations without dragging a wire URL through pool state — the
   * structured chapter response gives `terminalId` directly, so the
   * pre-#1054 `wsUrl` reverse-parse is gone.
   */
  private readonly lastTerminalIds = new Map<string, string>()
  private readonly closeHandlers = new Set<PoolCloseCallback>()
  private readonly closeUnsubs = new Map<string, Unsubscribe>()

  constructor(http: HttpTransport, ws: WsTransport) {
    super({ id: 'chapter' })
    this.http = http
    this.ws = ws
  }

  //////////////////////////////////////////////////////////////////////////////
  // Chapter / engagement
  //////////////////////////////////////////////////////////////////////////////

  /**
   * `GET /api/courses/:courseId/:partId/:chapterId` — the combined
   * chapter endpoint. Returns either `{kind:'ok', content, machines,
   * expiresAt}` for the chapter view, or `{kind:'conflict', current}`
   * for the dialog. Caller (the chapter page's fetcher) discriminates
   * by `kind` and renders accordingly.
   *
   * Per-call `timeout` of 180 s overrides the SPA transport's 30 s
   * default — this endpoint blocks on `hypervisor.boot`, which the
   * lab→hypervisor client already caps at 120 s and which has been
   * observed at 40 s+ on a Mac mini cold cache (#795). 180 s leaves
   * headroom while staying well below the engagement TTL so a
   * genuinely-stuck server still surfaces.
   */
  async getChapter(
    courseId: string,
    partId: string,
    chapterId: string,
  ): Promise<ChapterResponse> {
    return this.http.request(GetChapterRoute, {
      params: { courseId, partId, chapterId },
      timeout: 180_000,
    })
  }

  /**
   * `GET /api/courses/:courseId/:partId/assessment` — fetch the Part's
   * consolidated CAT assessment bank + course graph + chapters (for
   * remediation) + the learner's Part-level pass state (umbrella #1357).
   * The `PartAssessmentPage` drives the CAT off this. 404 surfaces here as
   * a thrown `HttpError` when the Part ships no assessment.
   *
   * @param courseId - the course slug
   * @param partId - the Part slug
   * @returns the Part-assessment payload (the bank is the serialized wire
   *   form; the page rehydrates it with `QuizSerde.rehydratePartAssessment`)
   */
  async getPartAssessment(
    courseId: string,
    partId: string,
  ): Promise<PartAssessmentResponse> {
    return this.http.request(GetPartAssessmentRoute, {
      params: { courseId, partId },
    })
  }

  /**
   * `POST /api/courses/engagement` — switch the learner's engagement
   * to a new chapter URL. Called when the conflict dialog's "Start
   * fresh" branch is taken: the SPA POSTs the desired chapter URL,
   * the server recipe-diffs against the prior engagement, lifecycles
   * machines, persists the new record, and echoes the resulting
   * machine list. Rate-limited per learner; 429 surfaces here as a
   * thrown `HttpError`.
   */
  async postEngagement(chapterUrl: string): Promise<EngagementResponse> {
    return this.http.request(PostEngagementRoute, {
      body: { chapterUrl },
    })
  }

  /**
   * `POST /api/machines/:machineId/suspend` — fire the "Pause"
   * affordance. Lab proxies to the hypervisor's voluntary-suspend
   * route which snapshots immediately, bypassing the post-detach
   * grace period. Idempotent on already-suspended / unknown ids.
   */
  async suspendMachine(machineId: string): Promise<void> {
    await this.http.request(PostSuspendMachineRoute, {
      params: { machineId },
    })
  }

  /**
   * `POST /api/machines/:machineId/restart` — the "start a fresh shell"
   * recovery for a terminally-`failed` machine (#1009 → pty close 4005).
   * Terminates the wedged VM server-side (hypervisor `shutdown` deletes
   * the session), then force-reopens the pooled Terminal's WS so the
   * follow-up `open` frame cold-boots a clean VM under the same
   * deterministic id — breaking the 4004/4005 attach-refusal loop.
   * No-op on an unknown / already-dropped machine.
   */
  async restartMachine(machineId: string): Promise<void> {
    await this.http.request(PostRestartMachineRoute, {
      params: { machineId },
    })
    const terminal = this.terminals.get(machineId)
    const terminalId = this.lastTerminalIds.get(machineId)
    if (terminal && terminalId) {
      terminal.attach(machineId, terminalId)
    }
  }

  /**
   * `POST /api/engagement/touch` — refresh the sliding engagement
   * TTL. Wired to the SPA's "Keep alive" button in the pre-expiry
   * warning banner. Returns the new deadline (ms since epoch).
   */
  async touchEngagement(): Promise<number> {
    const result = await this.http.request(PostEngagementTouchRoute)
    return result.expiresAt
  }

  /**
   * `POST /api/courses/:courseId/:partId/feedback` — submit the learner's
   * closing feedback for a Part (umbrella #1446 Part E). The closing
   * chapter's sole interaction; persists rating and/or comments and emits
   * the recalibration signal server-side. Throws an `HttpError` on 400
   * (empty body) / 401 so the form can surface + retry.
   */
  async submitPartFeedback(
    courseId: string,
    partId: string,
    feedback: PartFeedbackRequest,
  ): Promise<void> {
    await this.http.request(PostPartFeedbackRoute, {
      params: { courseId, partId },
      body: feedback,
    })
  }

  /**
   * `DELETE /api/courses/engagement` — release the learner's engagement
   * (umbrella #1446 Part C, #1572). Always releases the record; `intent`
   * decides the VMs. `'suspend'` (the involuntary tab-close / reload beacon)
   * leaves them to the hypervisor's 2-minute disconnect-grace snapshot so the
   * learner resumes where they left off; `'terminate'` (the default) shuts
   * them down promptly, for the deliberate "done" leaves ("Back to course", a
   * Part's closing chapter). Pass `{ keepalive: true }` from an unload handler
   * so the browser finishes the request after the document unloads (threaded
   * onto the fetch `keepalive` flag). Idempotent — safe with no active
   * engagement.
   *
   * @param opts - `keepalive` for unload beacons; `intent` to suspend vs
   *   terminate the VMs (defaults to terminate).
   */
  async leaveEngagement(opts?: {
    keepalive?: boolean
    intent?: 'suspend' | 'terminate'
  }): Promise<void> {
    await this.http.request(DeleteEngagementRoute, {
      query: { intent: opts?.intent },
      keepalive: opts?.keepalive ?? false,
    })
  }

  //////////////////////////////////////////////////////////////////////////////
  // Terminal pool
  //////////////////////////////////////////////////////////////////////////////

  /**
   * Diff the terminal pool against `machines` and bring it into sync.
   * New machineIds get a `Terminal` + typed `attach(machineId,
   * terminalId)`; dropped machineIds get `disconnect()` + removal;
   * matching machineIds with an unchanged `terminalId` are left alone
   * (no WS churn); matching machineIds with a changed `terminalId`
   * re-attach. The structured `{machineId, terminalId}` response
   * (#1054) is consumed directly — no wire-URL reverse-parse.
   */
  attachTerminals(machines: Machine[]): void {
    const nextIds = new Set(machines.map(m => m.machineId))

    // Drop machineIds no longer in the response.
    for (const machineId of [...this.terminals.keys()]) {
      if (!nextIds.has(machineId)) this.dropOne(machineId)
    }

    // Create / reconnect / no-op per new entry.
    for (const machine of machines) {
      const { machineId, terminalId } = machine
      const existing = this.terminals.get(machineId)
      if (existing) {
        if (this.lastTerminalIds.get(machineId) !== terminalId) {
          existing.attach(machineId, terminalId)
          this.lastTerminalIds.set(machineId, terminalId)
        }
        continue
      }
      const terminal = new Terminal(machineId, this.ws)
      const off = terminal.onClose((code, reason) => {
        for (const h of this.closeHandlers) {
          try {
            h(machineId, code, reason)
          } catch (err) {
            this.warn('onClose', CoreError.from(err))
          }
        }
      })
      this.closeUnsubs.set(machineId, off)
      this.terminals.set(machineId, terminal)
      this.lastTerminalIds.set(machineId, terminalId)
      terminal.attach(machineId, terminalId)
    }
  }

  /**
   * Tear down every Terminal in the pool. Called on chapter-page
   * unmount and on fatal-error paths. Idempotent on an empty pool.
   */
  detachTerminals(): void {
    for (const machineId of [...this.terminals.keys()]) {
      this.dropOne(machineId)
    }
  }

  /** Read the `Terminal` for a `machineId`, or `undefined` if absent. */
  terminalFor(machineId: string): Terminal | undefined {
    return this.terminals.get(machineId)
  }

  /** Snapshot of the currently-pooled machineIds, in iteration order. */
  machineIds(): string[] {
    return [...this.terminals.keys()]
  }

  /**
   * Subscribe to close events from any Terminal in the pool. The
   * handler receives `(machineId, code, reason)`. Used by the chapter
   * page to drive the eviction-toast surface.
   */
  onClose(handler: PoolCloseCallback): Unsubscribe {
    this.closeHandlers.add(handler)
    return () => {
      this.closeHandlers.delete(handler)
    }
  }

  //////////////////////////////////////////////////////////////////////////////
  // Private
  //////////////////////////////////////////////////////////////////////////////

  private dropOne(machineId: string): void {
    const off = this.closeUnsubs.get(machineId)
    if (off) {
      off()
      this.closeUnsubs.delete(machineId)
    }
    const terminal = this.terminals.get(machineId)
    if (terminal) {
      terminal.disconnect()
      this.terminals.delete(machineId)
    }
    this.lastTerminalIds.delete(machineId)
  }

  /** Safety-net teardown on parent lifecycle stop. */
  protected override async onStop(): Promise<void> {
    this.detachTerminals()
  }
}
